/**
 * Mission date/datetime parsing and display helpers.
 * dueDate may be legacy `YYYY-MM-DD` (local midnight) or full ISO datetime (UTC).
 */

const MS_MINUTE = 60 * 1000;
const MS_HOUR = 60 * MS_MINUTE;
const MS_DAY = 24 * MS_HOUR;

/**
 * Parse stored due or timestamp: legacy date-only uses local start-of-day.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseMissionDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const y = Number(ymd[1]);
    const m = Number(ymd[2]) - 1;
    const d = Number(ymd[3]);
    return new Date(y, m, d, 0, 0, 0, 0);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format due datetime for compact card/table display (relative + time when relevant).
 * @param {string|Date|null|undefined} dueDate
 * @returns {{ text: string, status: string }|null}
 */
export function formatDueDateDisplay(dueDate) {
  if (!dueDate) return null;
  const due = parseMissionDate(dueDate);
  if (!due) return null;

  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const timeOpts = { hour: 'numeric', minute: '2-digit' };
  const timeStr = due.toLocaleTimeString('en-US', timeOpts);
  const dateShort = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const dateWithYear = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (diffMs < 0) {
    const overdueMs = -diffMs;
    const overdueMins = Math.floor(overdueMs / MS_MINUTE);
    if (overdueMins < 60) {
      return { text: `${overdueMins}m overdue`, status: 'overdue' };
    }
    if (overdueMs < MS_DAY) {
      const h = Math.floor(overdueMins / 60);
      const m = overdueMins % 60;
      return { text: m ? `${h}h ${m}m overdue` : `${h}h overdue`, status: 'overdue' };
    }
    const days = Math.floor(overdueMs / MS_DAY);
    return { text: `${days}d overdue`, status: 'overdue' };
  }

  if (diffMs < MS_DAY) {
    const mins = Math.floor(diffMs / MS_MINUTE);
    if (mins < 60) return { text: `in ${mins}m`, status: 'soon' };
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return { text: m ? `in ${h}h ${m}m` : `in ${h}h`, status: 'soon' };
  }

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDayStart = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const dayDiff = Math.round((dueDayStart - todayStart) / MS_DAY);

  if (dayDiff === 0) return { text: `Today ${timeStr}`, status: 'today' };
  if (dayDiff === 1) return { text: `Tomorrow ${timeStr}`, status: 'soon' };
  if (dayDiff <= 3) return { text: `${dateShort}, ${timeStr}`, status: 'soon' };
  if (dayDiff <= 7) return { text: `${dateShort}, ${timeStr}`, status: 'upcoming' };
  return { text: `${dateWithYear}, ${timeStr}`, status: 'normal' };
}

/**
 * Full due datetime for tooltips / title attributes.
 * @param {string|Date|null|undefined} dueDate
 * @returns {string}
 */
export function formatDueDateFull(dueDate) {
  const d = parseMissionDate(dueDate);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Normalize Date or ISO-ish string for persistence (UTC ISO).
 * @param {string|Date|null|undefined} value
 * @returns {string|null}
 */
export function toIsoDateString(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}
